import { registerOp } from '../endpoints'
import { getTransport } from '../transport'
import { commitSuccess } from '../success-write'
import API from 'src/api'
import { answersRepository } from '../../repositories/AnswersRepository'
import { addonsRepository } from '../../repositories/AddonsRepository'
import { MetaRepository } from '../../repositories/MetaRepository'
import { buildBatchFormData, chunkRecordsForUpload } from '../builders/occurrences-builder'
import { makeResolveBlob } from '../../photos/resolveBlob'
import { PhotoStore } from '../../photos/PhotoStore'
import { getOfflineUserId } from '../../session'
import { StorageAdapter } from '../../storage/StorageAdapter'
import { rowsOf } from '../../storage/rows'
import { parseMaybeJson } from '../../storage/json-codec'
import { idemHeader, is2xx, throwFromResponse } from '../http-helpers'
import { dbgWarn } from '../../logger'
import { partitionAttachedRefs, buildFailedAddonsError } from '../failed-addons'
import { markSiblingSyncOpsDone, markDeadAnswerSyncOpsDone, snapshotSiblingSyncOps } from '../collapse-sibling-sync'
import { isServerId } from '../../repositories/ids'
import { IdMappingRepository } from '../../repositories/IdMappingRepository'

// Versión de la app (inlined por DefinePlugin en build). El backend GATEA el borrado offline de addons
// por app_version (AddonDeletionHelper: solo confía en el answer='' como "borra esto" de clientes cuyo
// builder manda '' SOLO en borrados intencionales). El body del op NUNCA la trae (se arma con ids
// mínimos), así que sin esto viajaba vacía -> el gate hacía skip SIEMPRE y las fotos/comentarios
// "borrados" reaparecían al reentrar (TKT-2280). Se resuelve al DRENAR: la versión que construye el
// payload es la que define la semántica de answer=''.
const APP_VERSION = import.meta.env.APP_VERSION || ''

/**
 * modules/occurrences.js — handlers de los ops de occurrence que F2 emite
 * (occurrence.syncOccurrence / .close / .saveSignature / .saveComments). Re-implementacion LIMPIA de
 * src/offline/modules/occurrences.js (REFERENCIA — NO importado).
 *
 * HIDRATAR-AL-DRENAR (PUSH-04/05): F2 escribio bodies MINIMOS (solo ids/refs). Estos handlers,
 * al drenar, LEEN las filas Tier A reales (answers/addons desde los repos F2) y construyen el
 * payload REST con el builder PURO. La red SOLO se toca via getTransport() (costura PUSH-05).
 *
 * CONTRATO commitSuccess LOCKED: tras un envio exitoso el handler llama commitSuccess(...) (el
 * success-write atomico) y DEVUELVE { committed: true }. El onSuccess de 04-01 corta-circuito con
 * `if (result && result.committed) return` ANTES de su propio flip — sin doble-write. Los handlers
 * que corto-circuitan por pre-check idempotente (marca meta presente, sin red) TAMBIEN llaman
 * commitSuccess(...) para finalizar la fila y devuelven { committed: true, ok: true }.
 *
 * Header Idempotency-Key = row.mutation_uuid en cada request (hardening; el backend hoy lo ignora).
 *
 * COSTURA resolveBlob (F3 -> F5): F3 pasaba un stub que devolvia null (datos+ref sincronizaban,
 * binario diferido). F5 inyecta el resolveBlob REAL (makeResolveBlob) que lee el binario del
 * backend de fotos activo (web idb / nativo FS) y lo da como Blob al builder PURO -> el binario
 * de foto ahora SI viaja en el multipart. La pureza del builder se mantiene (PUSH-05): el
 * resolveBlob se inyecta SOLO en este handler, nunca dentro del builder.
 */

const GRAPHQL_URL = API.GRAPHQL_URL

// resolveBlob REAL (F5): lee el binario de foto del backend activo y lo convierte a Blob.
const resolveBlob = makeResolveBlob()

/**
 * Normaliza la fila cruda de `answers` al shape de record que espera el builder.
 * `value`/`extra_properties` se guardan como TEXT -> parseMaybeJson (json-codec compartido) re-parsea
 * el JSON de objeto/array (form-reading objeto, checkbox/multinumeric array); si no es JSON, deja el
 * string crudo. Read-side SIMETRICO del toText de BaseRepository._buildUpsert (mismo modulo = sin drift).
 */
function answerRecord (row) {
  return {
    occurrence_id: row.occurrence_id,
    form_assignment_location_occurrence_id: row.form_assignment_location_occurrence_id ?? row.occurrence_id,
    item_id: row.item_id,
    value: parseMaybeJson(row.value),
    current_points: row.current_points,
    total_points: row.total_points,
    extra_properties: parseMaybeJson(row.extra_properties),
    image_ref: row.image_ref,
    local_path: row.local_path,
    created_at: row.created_at,
    updated_at: row.updated_at
  }
}

function addonRecord (row) {
  return {
    occurrence_id: row.occurrence_id,
    item_id: row.item_id,
    type: row.type,
    answer: parseMaybeJson(row.answer),
    answer_ref: row.answer_ref,
    image_ref: row.image_ref,
    local_path: row.local_path
  }
}

/**
 * EVIDENCIA (caso eviction, ortogonal al fix de sobrescritura). El builder OMITE las fotos cuyo
 * binario no resuelve para NO sobrescribir la imagen ya subida (invariante cero-perdida). La gran
 * mayoria de esas omisiones son LEGITIMAS (la foto ya se subio y se purgo). Pero si un binario
 * desaparecio ANTES de subirse (eviccion de idb / corrupcion), omitirlo lo deja sin subir y sin
 * rastro -> aqui se detecta y se registra para no fallar en silencio.
 *
 * BEST-EFFORT y NO-BLOQUEANTE: cualquier error aqui se traga (nunca rompe el drain — el envio de
 * datos ya fue exitoso). La nota durable reutiliza sync_notifications (mismo patron que el pull,
 * apply-server-rows.js) y es idempotente por (occurrence, kind).
 */
async function reportMissingPhotos (occId, omittedPhotoRefs) {
  try {
    const refs = (omittedPhotoRefs || []).map(o => o && o.ref).filter(Boolean)
    if (!refs.length) return
    const lost = await PhotoStore.unconfirmedRefs(refs)
    if (!lost.length) return // todas eran purgas legitimas post-subida; nada que reportar

    dbgWarn('photo', 'foto(s) sin binario recuperable y sin subida confirmada (posible pérdida)', { occurrence_id: occId, image_refs: lost })

    // Saca las refs perdidas del alcance de la PURGA: marca su photo_meta como 'missing' (la purga solo
    // toca 'pending'). Si la lectura falló TRANSITORIAMENTE (binario intacto) el siguiente intento/re-toma
    // la re-sube; si el binario ya no existe, backend.del sería no-op igual. Sin esto, el done de este
    // mismo drain habilitaba a la purga a destruir un binario que aún no subió (blocker review Hugo).
    for (const ref of lost) {
      try {
        await StorageAdapter.run("UPDATE photo_meta SET upload_status='missing' WHERE image_ref=? AND upload_status='pending'", [ref], true)
      } catch { /* best-effort */ }
    }

    // Rastro durable idempotente: una nota photo_missing por occurrence (sin re-insertar en cada drain).
    const existing = await StorageAdapter.query(
      "SELECT id FROM sync_notifications WHERE client_uuid=? AND kind='photo_missing' AND dismissed=0 LIMIT 1",
      [String(occId)]
    )
    if (!(existing && existing.values && existing.values.length)) {
      await StorageAdapter.run(
        'INSERT INTO sync_notifications (entity, client_uuid, kind, dismissed, created_at, user_id) VALUES (?,?,?,0,?,?)',
        ['occurrence', String(occId), 'photo_missing', Date.now(), getOfflineUserId()],
        true
      )
    }
  } catch (e) {
    dbgWarn('photo', 'reportMissingPhotos falló (ignorado)', e)
  }
}

/**
 * Tamaños (byte_length v9) de las fotos de la occurrence, por image_ref — para que el chunker trocee el
 * batch también por PESO (no solo por nº de archivos). Best-effort: si falla, el chunker cae a
 * solo-conteo (byte_length ausente = 0 = no acota por peso). Legacy pre-v9 (byte_length NULL) idem.
 */
async function loadPhotoSizes (occId) {
  const map = {}
  try {
    const res = await StorageAdapter.query(
      'SELECT image_ref, byte_length FROM photo_meta WHERE image_ref IN (' +
      'SELECT image_ref FROM addons WHERE occurrence_id=? AND image_ref IS NOT NULL ' +
      'UNION SELECT image_ref FROM answers WHERE occurrence_id=? AND image_ref IS NOT NULL)',
      [occId, occId]
    )
    for (const r of (res && res.values) || []) { if (r.image_ref) map[r.image_ref] = r.byte_length || 0 }
  } catch (e) {
    dbgWarn('photo', 'loadPhotoSizes falló (chunking cae a solo-conteo)', e)
  }
  return map
}

/**
 * AMPLIFICACIÓN DE SUBIDA (TKT-2240): refs de foto de la occurrence cuyo binario YA VIAJÓ.
 *
 * Este handler re-hidrata TODAS las answers+addons de la occurrence en CADA op, y resolveBlob
 * adjunta el binario de toda foto cuyo archivo siga en disco. Como la purga tiene una gracia de
 * 5 min (DONE_GRACE_MS), durante el llenado activo la MISMA foto se re-subía una vez por cada
 * guardado posterior dentro de esa ventana (medido: ~10-12 veces por foto). Aquí se listan las
 * fotos que ya subieron para NO volver a adjuntar su binario: el multipart sigue llevando el resto
 * del batch (texto, score) y el server, que upsertea por (item, occurrence), conserva la URL ya
 * guardada. El contrato del endpoint NO cambia; solo deja de viajar lo que ya está arriba.
 *
 * EVIDENCIA = `upload_status='uploaded'`, que sella markUploadedRefs() cuando el chunk que llevaba
 * ese binario respondió 2xx. Es un HECHO observado, no una inferencia.
 *
 * La versión anterior de este fix deducía la confirmación de las fechas —"existe un op done del
 * parent cuyo created_at es posterior a la captura de la foto"— y eso resultó ser una pérdida
 * silenciosa. Tres formas de romperlo, todas reales:
 *
 *   1. RE-TOMA DURANTE EL VUELO. El coalesce del outbox refresca `created_at` de la fila pending
 *      sin cambiar su `seq` (BaseRepository._saveOptimisticTx). Si el usuario re-toma la foto
 *      mientras el POST viaja, el op viejo queda con fecha nueva y markSiblingSyncOpsDone lo marca
 *      done por estar bajo boundarySeq. La foto NUEVA nunca viajó y quedaba "confirmada".
 *   2. RELOJ HACIA ATRÁS. Ambas fechas son de reloj de pared. Un ajuste de hora (NTP, RTC malo tras
 *      reboot) hacía que el done VIEJO fuera posterior a la captura NUEVA.
 *   3. reportMissingPhotos ES BEST-EFFORT. Si su UPDATE fallaba, la foto se quedaba en 'pending' y
 *      el predicado la daba por subida.
 *
 * En los tres casos la foto se saltaba, `lostCandidates` la filtraba (no había nota), y la purga
 * borraba el binario a los 5 min: pérdida irrecuperable y pegajosa. En master no ocurría porque la
 * re-subida que este PR elimina actuaba de red de seguridad. Sellar lo que de verdad se adjuntó
 * cierra los tres de una vez, y encima no depende del reloj.
 *
 * 'done' también cuenta: lo pone la purga DESPUÉS de subir y borrar el binario, así que resolveBlob
 * devolvería null igual; incluirlo evita una lectura de disco inútil.
 *
 * Best-effort: si la consulta falla se devuelve un set vacío = comportamiento previo (se
 * re-adjunta todo). Nunca bloquea el envío.
 */
async function loadConfirmedPhotoRefs (occId) {
  const refs = new Set()
  try {
    const res = await StorageAdapter.query(
      'SELECT pm.image_ref AS image_ref FROM photo_meta pm ' +
      "WHERE pm.upload_status IN ('uploaded','done') " +
      'AND pm.image_ref IN (' +
      'SELECT image_ref FROM addons WHERE occurrence_id=? AND image_ref IS NOT NULL ' +
      'UNION SELECT image_ref FROM answers WHERE occurrence_id=? AND image_ref IS NOT NULL)',
      [occId, occId]
    )
    for (const r of rowsOf(res)) { if (r.image_ref) refs.add(r.image_ref) }
  } catch (e) {
    dbgWarn('photo', 'loadConfirmedPhotoRefs falló (se re-adjuntan todos los binarios)', e)
  }
  return refs
}

/**
 * Sella las refs cuyo binario viajó DENTRO de un chunk que respondió 2xx (TKT-2240).
 *
 * Se llama por chunk y no al final: si el envío se corta a la mitad, lo ya subido queda sellado y
 * no vuelve a viajar en el reintento. 'uploaded' es un estado INTERMEDIO entre 'pending' y el
 * 'done' que pone la purga al borrar el binario — por eso purge.js lo incluye entre los purgables.
 *
 * Solo avanza desde 'pending'/'missing': nunca pisa un 'done' (la purga ya cerró ese ciclo).
 * Best-effort: si falla, el peor caso es volver a subir el binario, que es el comportamiento previo
 * al fix. Nunca rompe el envío, que a estas alturas ya fue aceptado por el server.
 */
async function markUploadedRefs (refs) {
  if (!refs?.length) { return }
  try {
    const placeholders = refs.map(() => '?').join(',')
    await StorageAdapter.run(
      "UPDATE photo_meta SET upload_status='uploaded' WHERE image_ref IN (" + placeholders + ') ' +
      "AND COALESCE(upload_status,'pending') IN ('pending','missing','uploaded')",
      refs
    )
  } catch (e) {
    dbgWarn('photo', 'markUploadedRefs falló (esas fotos se re-adjuntarán)', e)
  }
}

// ¿Quedan ops `ops` pendientes o en vuelo de la occurrence (con `beforeSeq`, solo las anteriores a esa
// seq)? occurrence_id solo vive en el body JSON (ver el comentario del cierre), así que se filtra aquí.
async function hasQueuedOccurrenceOps (occId, ops, { beforeSeq = null } = {}) {
  const placeholders = ops.map(() => '?').join(',')
  const rows = rowsOf(await StorageAdapter.query(
    `SELECT seq, body FROM outbox WHERE op IN (${placeholders}) AND status IN ('pending','in_flight')`,
    ops
  ))
  return rows.some((s) => {
    if (beforeSeq != null && !(s.seq < beforeSeq)) return false
    const b = parseMaybeJson(s.body)
    return b && String(b.occurrence_id) === String(occId)
  })
}

export function registerOccurrenceOps () {
  // ── occurrence.syncOccurrence ───────────────────────────────────────────────
  // HIDRATA answers + addons de la occurrence y arma el multipart. El server es idempotente
  // por UNIQUE(item_id, occ_id) (updateOrCreate) -> 200 siempre = done, incluso 200 {error}
  // de un item (el resto se persistio). No necesita id_mapping (dedup SQL-side).
  registerOp('occurrence.syncOccurrence', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const occId = body.occurrence_id

    // F-54: un NA de grupo anterior de la occurrence (p. ej. en backoff) va primero. Si este batch
    // subiera antes, el na-group borraría después las respuestas capturadas tras el NA. Espera como
    // el cierre (waitingDependency, sin consumir retries); un NA en dead-letter no bloquea.
    if (await hasQueuedOccurrenceOps(occId, ['occurrence.naGroup'], { beforeSeq: row.seq })) {
      const waitErr = new Error(`syncOccurrence occ=${occId} pospuesto: queda un NA de grupo anterior sin drenar`)
      waitErr.waitingDependency = true
      throw waitErr
    }

    // COTA O(n²) (Opción B): MAX(seq) ANTES del snapshot. Los siblings con seq <= boundarySeq ya
    // existían aquí -> su answer/foto viaja en este batch; los > boundarySeq (llenados durante el vuelo
    // HTTP) NO viajan y se dejan pending -> re-drenan idempotente (cero-pérdida). Ver markSiblingSyncOpsDone.
    const _bnd = rowsOf(await StorageAdapter.query('SELECT MAX(seq) AS m FROM outbox'))
    const boundarySeq = (_bnd[0] && _bnd[0].m) || row.seq
    // TKT-2363: seq + mutation_uuid de los siblings ANTES del snapshot. Una respuesta re-editada durante
    // el vuelo conserva su seq (coalesce en sitio) pero cambia de mutation_uuid -> no se colapsa.
    const siblingSnapshot = await snapshotSiblingSyncOps(occId, row.seq)

    const answerRows = await answersRepository.list('WHERE occurrence_id=?', [occId])
    const addonRows = await addonsRepository.list('WHERE occurrence_id=?', [occId])
    const records = [
      ...answerRows.map(answerRecord),
      ...addonRows.map(addonRecord)
    ]

    // CHUNKING (fix pérdida de fotos >20): PHP `max_file_uploads` (default 20) descarta EN SILENCIO los
    // file-parts que exceden el límite en UN multipart, y el endpoint responde 200 -> con >20 fotos las
    // que sobran nunca llegaban a S3 y se marcaban `done` (reproducido: 50 fotos, últimas 30 en 404). Se
    // trocea el batch en requests de <= MAX_FILES_PER_CHUNK archivos (y <= MAX_BYTES). Con <=15 archivos
    // es UN solo chunk (comportamiento idéntico, sin regresión). Idempotencia: el server hace
    // updateOrCreate por (item, occurrence) y cada chunk lleva una Idempotency-Key distinta; un reintento
    // re-manda todos los chunks sin duplicar dato.
    const sizeByRef = await loadPhotoSizes(occId)
    // ANTI-AMPLIFICACIÓN (TKT-2240): las fotos ya confirmadas no vuelven a viajar. El wrapper
    // devuelve null para ellas, que es EXACTAMENTE la señal que el builder ya sabe manejar sin
    // pisar nada en el server (answer: omite value/image_ref/file y conserva la URL; addon-foto:
    // omite el addon completo). Ver loadConfirmedPhotoRefs.
    const confirmedRefs = await loadConfirmedPhotoRefs(occId)
    const resolveBlobSkippingConfirmed = confirmedRefs.size
      ? async (record) => (record && confirmedRefs.has(record.image_ref) ? null : resolveBlob(record))
      : resolveBlob
    const chunks = chunkRecordsForUpload(records, sizeByRef, { skipFileRefs: confirmedRefs })
    const allOmitted = []
    const retryableRefs = []
    // Prefiere la del body si algún op la trajera; hoy siempre cae a APP_VERSION (el gate de borrado la exige).
    const appVersion = body.app_version || APP_VERSION
    for (let i = 0; i < chunks.length; i++) {
      const { formData, omittedPhotoRefs, attachedPhotoRefs, attachedByKey } = await buildBatchFormData(chunks[i], appVersion, resolveBlobSkippingConfirmed)
      allOmitted.push(...omittedPhotoRefs)
      const idemKey = chunks.length > 1 ? `${row.mutation_uuid}:c${i}` : row.mutation_uuid
      const res = await getTransport().post(
        `${GRAPHQL_URL}occurrences/save-offline-answers-copy`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data', 'Idempotency-Key': idemKey } }
      )
      // no-2xx -> que el clasificador decida.
      if (!is2xx(res)) throwFromResponse(res)
      // SAFEGUARD sync-siempre (integridad v9): este endpoint responde HTTP 200 con {"error": "..."}
      // cuando el guardado server-side FALLA (el catch de OfflineControllerCopy devuelve 200 con el
      // mensaje — p.ej. S3 PutObject sin bucket/permiso). Antes "200 siempre = done": el op se marcaba
      // sincronizado, la purga borraba el binario local a los 5 min y la evidencia se PERDÍA en
      // silencio (reproducido). Ahora un 200-con-error se lanza como Error SIN status -> classify lo
      // trata como retry transitorio (backoff; dead-letter VISIBLE tras MAX_RETRIES si es permanente).
      // El reintento es seguro: el server hace updateOrCreate idempotente por (item, occurrence).
      if (res.data && typeof res.data === 'object' && res.data.error) {
        const detail = String(res.data.error).slice(0, 200)
        throw new Error(`save-offline-answers-copy 200 con error server-side (chunk ${i + 1}/${chunks.length}): ${detail}`)
      }
      // El chunk fue aceptado de verdad (2xx y sin error server-side): recién AQUÍ se sella lo que
      // llevaba. Antes de este punto no hay evidencia de que la foto haya llegado.
      const failedList = res.data?.failed
      const { sealRefs, retryableRefs: chunkRetryableRefs } = partitionAttachedRefs(failedList, attachedPhotoRefs, attachedByKey)
      await markUploadedRefs(sealRefs)
      retryableRefs.push(...chunkRetryableRefs)
    }

    if (retryableRefs.length) {
      throw buildFailedAddonsError(retryableRefs)
    }

    await commitSuccess({ seq: row.seq, entity: 'occurrence', clientUuid: row.client_uuid, serverId: null, metaKeys: [] })
    // Evidencia best-effort de fotos omitidas sin subida confirmada (NO bloquea: el envio ya fue ok).
    // Las que se omitieron A PROPÓSITO (subida ya confirmada, TKT-2240) NO son candidatas a pérdida:
    // unconfirmedRefs solo considera confirmada la que ya purgó (upload_status='done'), así que una
    // foto subida pero aún dentro de la gracia de 5 min de la purga entraría como falsa pérdida y
    // levantaría una nota photo_missing visible al usuario. Se filtran antes de reportar.
    const lostCandidates = allOmitted.filter((o) => !(o && confirmedRefs.has(o.ref)))
    await reportMissingPhotos(occId, lostCandidates)
    // OPTIMIZACIÓN O(n²): los demás syncOccurrence pending de esta occ (que ya existían al snapshot,
    // seq <= boundarySeq) ya viajaron en ESTE batch íntegro -> marcarlos done evita re-enviar todo por
    // cada item. DESPUÉS de reportMissingPhotos a propósito: la omisión de la occ queda registrada
    // ANTES de que los siblings se vuelvan purgables.
    await markSiblingSyncOpsDone(occId, row.seq, boundarySeq, { snapshot: siblingSnapshot })
    await markDeadAnswerSyncOpsDone(occId, boundarySeq)
    return { committed: true, ok: true }
  })

  // ── occurrence.close ────────────────────────────────────────────────────────
  // PRE-CHECK occ-closed:<occId> -> idempotente sin red. El cierre full multi-substep
  // (answers->saveEditingTasks->notify) es F5/MOD-01; F3 cablea save-and-close + la marca.

  // FIX 9 (envenenamiento de action_plan_tasks.id, 2026-08-17): las tasksEditing del cierre traen el
  // client_uuid como `id` (la llave optimista de UI, deliberada — ver addOptimisticTask). Mandarlo
  // crudo hacía que el backend lo insertara como PK: con sql no estricto el uuid se castea a número
  // (patrón "3e58…" = notación científica) y clavó el AUTO_INCREMENT en el tope del tipo, dos veces.
  // El backend ya se defiende (#1831: loggea y crea la task completa), pero crear != actualizar: sin
  // remapear, cada cierre DUPLICA la task en vez de editar la que task.create->fromForms ya creó.
  // La reescritura vive aquí, al DRENAR (no al encolar: cuando el usuario presiona Finalizar el
  // mapping casi nunca existe todavía). Por task:
  //   - id numérico (isServerId) -> tal cual;
  //   - client_uuid con mapping (su task.create ya drenó) -> se sustituye por el server_id;
  //   - sin mapping y el task.create sigue pending/in_flight -> waitingDependency: el cierre espera
  //     sin consumir retries (mismo patrón que el chequeo de answers del handler);
  //   - sin mapping y sin create en cola (dead-letter, o SQLite falló al crear) -> viaja con el uuid
  //     a PROPÓSITO: es la única copia del dato y el backend (#1831) la crea completa.
  async function resolveTasksEditingIds (occId, tasksEditing) {
    const pendingCreates = new Set(rowsOf(await StorageAdapter.query(
      "SELECT client_uuid FROM outbox WHERE op='task.create' AND status IN ('pending','in_flight')"
    )).map((r) => String(r.client_uuid)))

    const resolved = []
    for (const task of tasksEditing) {
      if (!task || isServerId(task.id)) {
        resolved.push(task)
        continue
      }
      const clientUuid = String(task.client_uuid || task.id)
      const serverId = await IdMappingRepository.serverIdFor('task', clientUuid)
      if (serverId != null) {
        resolved.push({ ...task, id: serverId })
        continue
      }
      if (pendingCreates.has(clientUuid)) {
        const waitErr = new Error(`close occ=${occId} pospuesto: task.create ${clientUuid} sin drenar (aún no hay server_id)`)
        waitErr.waitingDependency = true
        throw waitErr
      }
      resolved.push(task)
    }
    return resolved
  }

  registerOp('occurrence.close', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const occId = body.occurrence_id
    const closedKey = MetaRepository.occClosedKey(occId)

    if (await MetaRepository.has(closedKey)) {
      // re-send tras respuesta perdida: ya cerrada -> finalizar la fila sin red.
      await commitSuccess({ seq: row.seq, metaKeys: [closedKey] })
      return { committed: true, ok: true }
    }

    // F3 (PR review): el drain toma ops "due" por seq, pero una sibling occurrence.syncOccurrence en
    // BACKOFF (next_attempt_at futuro) dejaría pasar este close -> la occurrence se cerraría en el
    // server SIN las answers/fotos del batch fallido (score y auditoría incompletos). Si quedan
    // syncOccurrence de ESTA occurrence sin terminar (pending/in_flight), el close ESPERA:
    // waitingDependency reagenda sin consumir retries (classify -> throttle). Una sibling 'dead' NO
    // bloquea (su pérdida ya quedó visible en el dead-letter drawer; bloquear el cierre para siempre
    // sería peor).
    // El filtro por occurrence_id NO se puede hacer en SQL con el esquema actual [review Hugo r3 #11]:
    // occurrence_id solo vive dentro del `body` JSON, y las otras columnas no discriminan — `entity` es
    // 'occurrence' para las 3 fuentes, y `client_uuid` es el uuid PROPIO de la answer/addon
    // (`ans:<occ>:<item>`), asi que filtrar por el dejaria FUERA justo las filas que este chequeo existe
    // para detectar. Queda O(filas pending/in_flight del device); el fix de raiz es una columna indexada
    // dedicada (migracion futura, trackeada aparte).
    // F-97: los comentarios adicionales (occurrence.saveComments) también van antes del cierre.
    const answersStillQueued = await hasQueuedOccurrenceOps(occId, ['occurrence.syncOccurrence', 'occurrence.naGroup', 'occurrence.saveComments'])
    if (answersStillQueued) {
      const waitErr = new Error(`close occ=${occId} pospuesto: quedan answers/addons, NA de grupo o comentarios de la occurrence sin drenar`)
      waitErr.waitingDependency = true
      throw waitErr
    }

    // FIX 7: si la occurrence trae tasksEditing, se guardan ANTES del save-and-close, igual que el
    // cierre online legacy (OccurrencesServices.saveEditingTasks(occId, tasksEditing) ->
    // POST tasks/<occId>/occurrenceSave). Sin esto las ediciones de tareas del cierre se perdían al
    // cerrar offline. Idempotencia: marca occ-tasks-saved:<occId> para no reenviar en un retry tras
    // respuesta perdida (mismo patrón que occClosedKey). NO se commitea aquí: el commit final del op
    // ocurre tras el save-and-close exitoso (la marca de tasks viaja en metaKeys de ese commit).
    const tasksSavedKey = `occ-tasks-saved:${occId}`
    const metaKeysToCommit = [closedKey]
    if (Array.isArray(body.tasksEditing) && body.tasksEditing.length && !(await MetaRepository.has(tasksSavedKey))) {
      // FIX 9: remapear client_uuid -> server_id ANTES de POSTear (ver resolveTasksEditingIds arriba).
      const tasksToSave = await resolveTasksEditingIds(occId, body.tasksEditing)
      const tasksRes = await getTransport().post(
        `${GRAPHQL_URL}tasks/${occId}/occurrenceSave`,
        tasksToSave,
        idemHeader(row)
      )
      if (!is2xx(tasksRes)) throwFromResponse(tasksRes)
      metaKeysToCommit.push(tasksSavedKey)
    }

    const res = await getTransport().post(
      `${GRAPHQL_URL}occurrences/${occId}/form-assignments/save-and-close`,
      body.payload ?? {},
      idemHeader(row)
    )
    if (!is2xx(res)) throwFromResponse(res)

    await commitSuccess({ seq: row.seq, metaKeys: metaKeysToCommit })
    return { committed: true, ok: true }
  })

  // ── occurrence.naGroup ───────────────────────────────────────────────────────
  // NA de un grupo capturado con la base local abierta: el mismo POST na-group del camino en línea
  // (borra y vuelve a crear como `na` la respuesta de cada ítem del grupo).
  registerOp('occurrence.naGroup', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const occId = body.occurrence_id
    const res = await getTransport().post(
      `${GRAPHQL_URL}occurrences/${occId}/na-group`,
      { group_id: body.group_id },
      idemHeader(row)
    )
    if (!is2xx(res)) throwFromResponse(res)

    await commitSuccess({ seq: row.seq })
    return { committed: true, ok: true }
  })

  // ── occurrence.saveComments (F-97) ───────────────────────────────────────────
  // Latest-state: PUT comments/<occ> con el ÚLTIMO texto (el repo coalesce las ediciones pending). Es el
  // mismo endpoint del camino en línea y reemplaza el texto entero, así que reintentarlo es seguro. El
  // backend (OccurrenceController@updateComments) responde 200 también cuando el save() falla, con
  // `result: 'update operation has not been updated'`: eso NO es éxito, se lanza como 5xx para reintentar.
  registerOp('occurrence.saveComments', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const occId = body.occurrence_id
    const res = await getTransport().put(
      `${GRAPHQL_URL}comments/${occId}`,
      { id: Number(occId), comments: body.comments ?? '' },
      idemHeader(row)
    )
    if (!is2xx(res)) throwFromResponse(res)
    if (res?.data?.result === 'update operation has not been updated') throwFromResponse({ ...res, status: 500 })

    await commitSuccess({ seq: row.seq })
    return { committed: true, ok: true }
  })

  // ── occurrence.saveSignature ─────────────────────────────────────────────────
  // Append-only: PUT a la form-assignment con { signatures:[...] }. Sin marca de dedup mas
  // alla del status='done' de la fila (cada firma es su propia fila de outbox).
  registerOp('occurrence.saveSignature', async (row) => {
    const body = JSON.parse(row.body || '{}')
    const occId = body.occurrence_id
    const payload = body.payload ?? {}
    const assignmentId = payload.form_assignment_id ?? body.form_assignment_id ?? ''

    const res = await getTransport().put(
      `${GRAPHQL_URL}occurrences/${occId}/form-assignments/${assignmentId}`,
      payload,
      idemHeader(row)
    )
    if (!is2xx(res)) throwFromResponse(res)

    // F-75: con una sola firma en el payload, su id de servidor queda en id_mapping para poder borrarla
    // sin recargar. Las ops viejas con varias firmas en el payload no se mapean (no se sabe cuál es).
    const sentSignatures = Array.isArray(payload.signatures) ? payload.signatures : []
    const createdSignatures = res?.data?.data?.signatures
    const createdId = Array.isArray(createdSignatures) ? createdSignatures[0]?.id : null
    const mapping = sentSignatures.length === 1 && isServerId(createdId)
      ? { entity: 'signature', clientUuid: row.client_uuid, serverId: Number(createdId) }
      : {}
    await commitSuccess({ seq: row.seq, metaKeys: [], ...mapping })
    return { committed: true, ok: true }
  })
}
